import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "../../components/Sidebar";
import { useAuth, API } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  Banknote, Plus, Loader2, Search, Trash2, Pencil, Download, RefreshCw, Send,
  Building2, Users, Plug, Printer, CheckCircle2, FileDown, X, LayoutTemplate, RotateCcw, ChevronsUpDown
} from "lucide-react";

const fmtFecha = (s) => (s ? new Date(s).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" }) : "—");
const fmtDia = (s) => (s ? new Date(s + "T12:00:00").toLocaleDateString("es-DO") : "—");
const fmtMonto = (n) => `RD$ ${(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2 })}`;

const ESTADO_CHEQUE = {
  borrador: "bg-slate-200 text-slate-700",
  en_cola: "bg-amber-100 text-amber-800",
  registrado: "bg-emerald-100 text-emerald-800",
  impreso: "bg-blue-100 text-blue-700",
  error: "bg-red-100 text-red-700",
};

const FORM_VACIO = { cuenta_banco: "", beneficiario: "", fecha: new Date().toISOString().slice(0, 10), numero: "", concepto: "", lineas: [{ cuenta: "", monto: "", memo: "" }] };

const CAMPOS_LAYOUT = [
  ["empresa", "Nombre empresa (preimpreso)", "QASESORES, S.R.L."],
  ["numero", "No. de cheque (preimpreso)", "001159"],
  ["fecha", "Fecha (dígitos DIA MES AÑO)", "2 0 0 8 2 0 2 6"],
  ["beneficiario", "Beneficiario", "Monserrat Montes de Oca Hiraldo"],
  ["monto", "Monto en números", "2,385.41"],
  ["monto_letras", "Monto en letras", "Dos Mil Trescientos Ochenta Y Cinco Con 41/100 **********"],
  ["banco", "Cuenta de banco (preimpreso)", "Banco Popular"],
  ["concepto", "Concepto (va en el advice)", "Concepto del cheque"],
  ["voucher", "Advice (fecha · concepto · monto)", "20-ago.-2026    Servicios de Administración de Pólizas    2,385.41"],
];
const ESCALA = 3.2; // px por mm (hoja carta: 216mm de ancho)

function useDebounced(value, ms = 450) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Selector con campo de búsqueda propio (escriba para filtrar en tiempo real)
export function ComboSearch({ value, onChange, opciones, placeholder = "Seleccione...", testid, className = "" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const qD = useDebounced(q, 300);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtradas = qD.trim() ? opciones.filter((o) => (o.label || "").toLowerCase().includes(qD.trim().toLowerCase())) : opciones;
  const sel = opciones.find((o) => o.value === value);
  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" onClick={() => { setOpen((v) => !v); setQ(""); }} data-testid={testid}
        className="w-full h-10 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm text-left flex items-center justify-between gap-2">
        <span className={`truncate ${sel || value ? "dark:text-white" : "text-slate-400"}`}>{sel ? sel.label : (value || placeholder)}</span>
        <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[240px] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl">
          <div className="relative p-1.5 border-b border-slate-100 dark:border-slate-800">
            <Search className="w-3.5 h-3.5 absolute left-4 top-4 text-slate-400" />
            <Input autoFocus className="h-8 pl-8 text-sm" placeholder="Escriba para buscar..." value={q}
              onChange={(e) => setQ(e.target.value)} data-testid={testid ? `${testid}-buscar` : undefined} />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {value && (
              <button type="button" className="w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                onClick={() => { onChange(""); setOpen(false); }}>— Quitar selección —</button>
            )}
            {filtradas.length === 0 ? (
              <p className="px-3 py-2 text-xs text-slate-400">Sin resultados para "{q}"</p>
            ) : filtradas.slice(0, 400).map((o) => (
              <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 dark:hover:bg-slate-800 ${o.value === value ? "bg-blue-50 dark:bg-slate-800 font-medium" : "dark:text-slate-200"}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ChequesQBPage() {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());

  const [cheques, setCheques] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [cuentas, setCuentas] = useState([]);
  const [suplidores, setSuplidores] = useState([]);
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buscarSup, setBuscarSup] = useState("");
  const [buscarCta, setBuscarCta] = useState("");
  const buscarSupD = useDebounced(buscarSup);
  const buscarCtaD = useDebounced(buscarCta);

  // Nuevo/editar cheque
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [eliminar, setEliminar] = useState(null);

  // Config conexión (admin)
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ username: "", password: "", run_every_min: 10 });
  const [guardandoCfg, setGuardandoCfg] = useState(false);

  const fetchCheques = useCallback(async (est = filtroEstado) => {
    try {
      const params = est ? `?estado=${est}` : "";
      const r = await axios.get(`${API}/quickbooks/cheques${params}`, getAuthHeaders());
      setCheques(r.data || []);
    } catch { toast.error("Error cargando cheques"); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders, filtroEstado]);

  const fetchCatalogos = useCallback(async () => {
    try {
      const [rc, rs, re] = await Promise.all([
        axios.get(`${API}/quickbooks/cuentas`, getAuthHeaders()),
        axios.get(`${API}/quickbooks/suplidores`, getAuthHeaders()),
        axios.get(`${API}/quickbooks/estado`, getAuthHeaders()),
      ]);
      setCuentas(rc.data || []); setSuplidores(rs.data || []); setEstado(re.data);
    } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCheques();
    fetchCatalogos();
    fetchLayout();
    if (isAdmin) {
      axios.get(`${API}/quickbooks/config`, getAuthHeaders()).then((r) => {
        setConfig(r.data);
        if (r.data?.configurado) setConfigForm((f) => ({ ...f, username: r.data.username, run_every_min: r.data.run_every_min || 10 }));
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cuentasBanco = cuentas.filter((c) => c.tipo === "Bank");
  const cuentasGasto = cuentas.filter((c) => c.tipo !== "Bank");
  const totalForm = form.lineas.reduce((s, l) => s + (parseFloat(l.monto) || 0), 0);

  const abrirNuevo = async () => {
    setQbSel({ banco: "", beneficiario: "", txn_id: "" });
    setQbBusqueda("");
    setShowQB(true);
    try { const r = await axios.get(`${API}/quickbooks/cheques-qb`, getAuthHeaders()); setChequesQB(r.data || []); } catch { /* silencioso */ }
  };
  const abrirEditar = (ch) => {
    setEditId(ch.id);
    setForm({ cuenta_banco: ch.cuenta_banco, beneficiario: ch.beneficiario, fecha: ch.fecha, numero: ch.numero || "", concepto: ch.concepto || "", lineas: ch.lineas.map((l) => ({ ...l, monto: String(l.monto) })) });
    setShowForm(true);
  };

  // Traer cheque registrado en QuickBooks (flujo QB Enlace)
  const [showQB, setShowQB] = useState(false);
  const [chequesQB, setChequesQB] = useState([]);
  const [qbSel, setQbSel] = useState({ banco: "", beneficiario: "", txn_id: "" });
  const [importando, setImportando] = useState(false);
  const [qbBusqueda, setQbBusqueda] = useState("");
  const qbBusquedaD = useDebounced(qbBusqueda);

  const importarCheque = async () => {
    try {
      setImportando(true);
      const r = await axios.post(`${API}/quickbooks/cheques/importar`, { txn_id: qbSel.txn_id }, getAuthHeaders());
      toast.success(r.data.existente ? "Este cheque ya estaba traído; generando PDF" : "Cheque traído de QuickBooks; generando PDF");
      setShowQB(false);
      fetchCheques();
      descargarPdf(r.data.cheque);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al traer el cheque"); } finally { setImportando(false); }
  };

  // Layout de impresión (pestaña Formato)
  const [layout, setLayout] = useState(null);
  const [campoSel, setCampoSel] = useState("beneficiario");
  const [guardandoLayout, setGuardandoLayout] = useState(false);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const fetchLayout = useCallback(async () => {
    try { const r = await axios.get(`${API}/quickbooks/layout`, getAuthHeaders()); setLayout(r.data.campos); } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  const startDrag = (e, id) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const c = layout[id];
    dragRef.current = { id, dx: e.clientX - (rect.left + c.x * ESCALA), dy: e.clientY - (rect.top + c.y * ESCALA) };
    setCampoSel(id);
  };
  const moverDrag = (e) => {
    const d = dragRef.current;
    if (!d || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(205, (e.clientX - rect.left - d.dx) / ESCALA));
    const y = Math.max(2, Math.min(130, (e.clientY - rect.top - d.dy) / ESCALA));
    setLayout((L) => ({ ...L, [d.id]: { ...L[d.id], x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 } }));
  };
  const finDrag = () => { dragRef.current = null; };

  const setCampo = (id, campo, valor) => setLayout((L) => ({ ...L, [id]: { ...L[id], [campo]: valor } }));

  const guardarLayout = async () => {
    try {
      setGuardandoLayout(true);
      await axios.post(`${API}/quickbooks/layout`, { campos: layout }, getAuthHeaders());
      toast.success("Formato de impresión guardado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); } finally { setGuardandoLayout(false); }
  };

  const resetLayout = async () => {
    try {
      const r = await axios.post(`${API}/quickbooks/layout/reset`, {}, getAuthHeaders());
      setLayout(r.data.campos);
      toast.success("Formato restaurado al predeterminado");
    } catch { toast.error("Error"); }
  };

  const pdfPrueba = async () => {
    try {
      const res = await axios.get(`${API}/quickbooks/layout/pdf-prueba`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "cheque_prueba.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al generar el PDF de prueba"); }
  };

  const guardarCheque = async () => {
    try {
      setGuardando(true);
      const payload = { ...form, lineas: form.lineas.map((l) => ({ ...l, monto: parseFloat(l.monto) || 0 })) };
      if (editId) await axios.put(`${API}/quickbooks/cheques/${editId}`, payload, getAuthHeaders());
      else await axios.post(`${API}/quickbooks/cheques`, payload, getAuthHeaders());
      toast.success(editId ? "Cheque actualizado" : "Cheque guardado en borrador");
      setShowForm(false);
      fetchCheques();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); } finally { setGuardando(false); }
  };

  const encolar = async (ch) => {
    try {
      const r = await axios.post(`${API}/quickbooks/cheques/${ch.id}/encolar`, {}, getAuthHeaders());
      toast.success(r.data.message);
      fetchCheques();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const eliminarCheque = async () => {
    try {
      await axios.delete(`${API}/quickbooks/cheques/${eliminar.id}`, getAuthHeaders());
      toast.success("Cheque eliminado");
      setEliminar(null);
      fetchCheques();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const descargarPdf = async (ch) => {
    try {
      const res = await axios.get(`${API}/quickbooks/cheques/${ch.id}/pdf`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cheque_${ch.numero || ch.id.slice(0, 6)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al generar el PDF"); }
  };

  const marcarImpreso = async (ch) => {
    try {
      await axios.post(`${API}/quickbooks/cheques/${ch.id}/marcar-impreso`, {}, getAuthHeaders());
      toast.success("Cheque marcado como impreso");
      fetchCheques();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const syncAhora = async () => {
    try {
      const r = await axios.post(`${API}/quickbooks/sync-now`, {}, getAuthHeaders());
      toast.success(r.data.message);
    } catch { toast.error("Error"); }
  };

  const guardarConfig = async () => {
    try {
      setGuardandoCfg(true);
      await axios.post(`${API}/quickbooks/config`, configForm, getAuthHeaders());
      toast.success("Conexión guardada. Descargue el archivo .qwc e impórtelo en el Web Connector.");
      const r = await axios.get(`${API}/quickbooks/config`, getAuthHeaders());
      setConfig(r.data);
      setConfigForm((f) => ({ ...f, password: "" }));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setGuardandoCfg(false); }
  };

  const descargarQwc = async () => {
    try {
      const res = await axios.get(`${API}/quickbooks/qwc`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "qasesores_quickbooks.qwc"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Guarde primero el usuario y la clave"); }
  };

  const setLinea = (i, campo, valor) => {
    const lineas = form.lineas.map((l, idx) => (idx === i ? { ...l, [campo]: valor } : l));
    setForm({ ...form, lineas });
  };

  const supFiltrados = suplidores.filter((s) => (s.nombre || "").toLowerCase().includes(buscarSupD.toLowerCase()));
  const ctaFiltradas = cuentas.filter((c) => (c.nombre || "").toLowerCase().includes(buscarCtaD.toLowerCase()));

  const qbQ = qbBusquedaD.toLowerCase();
  const bancosQB = [...new Set(chequesQB.map((c) => c.banco).filter(Boolean))].sort();
  const benefQB = [...new Set(chequesQB.filter((c) => !qbSel.banco || c.banco === qbSel.banco).map((c) => c.beneficiario).filter(Boolean))].sort();
  const chequesFiltradosQB = chequesQB.filter((c) =>
    (!qbSel.banco || c.banco === qbSel.banco) &&
    (!qbSel.beneficiario || c.beneficiario === qbSel.beneficiario) &&
    (!qbQ || (c.beneficiario || "").toLowerCase().includes(qbQ) || String(c.numero || "").toLowerCase().includes(qbQ) || String(c.monto || "").includes(qbQ)));
  const chequeSelQB = chequesQB.find((c) => c.txn_id === qbSel.txn_id);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="cheques-qb-page">
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-[#0C314C] via-[#123f61] to-[#1a5a8a] p-6 shadow-lg">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><Banknote className="w-6 h-6 text-white" /></span>
            Cheques — QuickBooks
          </h1>
          <p className="text-blue-100/90 mt-1.5">Programe e imprima cheques; se registran en QuickBooks Enterprise vía Web Connector</p>
        </div>

        <Tabs defaultValue="cheques">
          <TabsList className="flex-wrap h-auto" data-testid="cheques-tabs">
            <TabsTrigger value="cheques" data-testid="tab-cheques"><Banknote className="w-4 h-4 mr-1" />Cheques</TabsTrigger>
            <TabsTrigger value="suplidores" data-testid="tab-suplidores"><Users className="w-4 h-4 mr-1" />Beneficiarios</TabsTrigger>
            <TabsTrigger value="cuentas" data-testid="tab-cuentas"><Building2 className="w-4 h-4 mr-1" />Cuentas</TabsTrigger>
            <TabsTrigger value="formato" data-testid="tab-formato"><LayoutTemplate className="w-4 h-4 mr-1" />Formato</TabsTrigger>
            {isAdmin && <TabsTrigger value="conexion" data-testid="tab-conexion"><Plug className="w-4 h-4 mr-1" />Conexión QB</TabsTrigger>}
          </TabsList>

          {/* ===== CHEQUES ===== */}
          <TabsContent value="cheques" className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2 justify-between items-end">
              <div className="flex flex-wrap gap-1.5">
                {[["", "Todos"], ["borrador", "Borradores"], ["en_cola", "En cola"], ["registrado", "Registrados"], ["impreso", "Impresos"], ["error", "Con error"]].map(([v, l]) => (
                  <Button key={v} size="sm" variant={filtroEstado === v ? "default" : "outline"}
                    className={filtroEstado === v ? "bg-[#0C314C] hover:bg-[#0a2940] text-white" : ""}
                    onClick={() => { setFiltroEstado(v); fetchCheques(v); }} data-testid={`filtro-cheques-${v || "todos"}`}>{l}</Button>
                ))}
              </div>
              <Button onClick={abrirNuevo} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="nuevo-cheque-btn">
                <Plus className="w-4 h-4 mr-1" />Nuevo Cheque
              </Button>
            </div>
            {estado && !estado.configurado && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-200" data-testid="alerta-sin-conexion">
                La conexión con QuickBooks aún no está configurada{isAdmin ? " — configúrela en la pestaña Conexión QB." : ". Solicite al administrador configurarla."} Puede crear cheques en borrador mientras tanto.
              </div>
            )}
            {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div> : (
              <Card className="shadow-md overflow-hidden"><CardContent className="p-0 overflow-x-auto max-h-[65vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white">
                    <th className="p-3 font-semibold whitespace-nowrap">Fecha</th><th className="p-3 font-semibold whitespace-nowrap">No.</th>
                    <th className="p-3 font-semibold whitespace-nowrap">Beneficiario</th><th className="p-3 font-semibold whitespace-nowrap">Banco</th>
                    <th className="p-3 font-semibold whitespace-nowrap">Monto</th><th className="p-3 font-semibold whitespace-nowrap">Creado por</th>
                    <th className="p-3 font-semibold whitespace-nowrap">Estado</th><th className="p-3 font-semibold whitespace-nowrap">Acción</th>
                  </tr></thead>
                  <tbody data-testid="cheques-body">
                    {cheques.length === 0 ? (
                      <tr><td colSpan={8} className="p-8 text-center text-slate-400">No hay cheques {filtroEstado ? "en este estado" : "programados"}</td></tr>
                    ) : cheques.map((ch, i) => (
                      <tr key={ch.id} className={`border-b hover:bg-blue-50/60 dark:hover:bg-slate-800/60 ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`} data-testid={`cheque-row-${ch.id}`}>
                        <td className="p-2.5 whitespace-nowrap text-slate-500">{fmtDia(ch.fecha)}</td>
                        <td className="p-2.5 font-mono">{ch.numero || "—"}</td>
                        <td className="p-2.5 font-medium dark:text-white">{ch.beneficiario}</td>
                        <td className="p-2.5 text-slate-500">{ch.cuenta_banco}</td>
                        <td className="p-2.5 font-semibold whitespace-nowrap">{fmtMonto(ch.monto)}</td>
                        <td className="p-2.5 text-slate-500">{ch.creado_por_nombre}</td>
                        <td className="p-2.5">
                          <Badge className={ESTADO_CHEQUE[ch.estado] || ""} title={ch.qb_error || ""}>{(ch.estado || "").replace("_", " ")}</Badge>
                          {ch.qb_error && <p className="text-xs text-red-600 mt-0.5 max-w-[180px]">{ch.qb_error}</p>}
                        </td>
                        <td className="p-2.5">
                          <div className="flex gap-1 flex-wrap">
                            {["borrador", "error"].includes(ch.estado) && (
                              <>
                                <Button size="sm" variant="outline" className="h-8 px-2 text-emerald-700" title="Registrar en QuickBooks" onClick={() => encolar(ch)} data-testid={`encolar-btn-${ch.id}`}>
                                  <Send className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 px-2 text-indigo-600" title="Editar" onClick={() => abrirEditar(ch)} data-testid={`editar-cheque-btn-${ch.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="outline" className="h-8 px-2 text-[#0C314C]" title="Imprimir / PDF" onClick={() => descargarPdf(ch)} data-testid={`pdf-cheque-btn-${ch.id}`}>
                              <Printer className="w-4 h-4" />
                            </Button>
                            {ch.estado === "registrado" && (
                              <Button size="sm" variant="outline" className="h-8 px-2 text-blue-600" title="Marcar como impreso" onClick={() => marcarImpreso(ch)} data-testid={`impreso-btn-${ch.id}`}>
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                            )}
                            {(["borrador", "error"].includes(ch.estado) || isAdmin) && (
                              <Button size="sm" variant="outline" className="h-8 px-2 text-red-600 border-red-200" title="Eliminar" onClick={() => setEliminar(ch)} data-testid={`eliminar-cheque-btn-${ch.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>
            )}
          </TabsContent>

          {/* ===== SUPLIDORES ===== */}
          <TabsContent value="suplidores" className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2 justify-between items-center">
              <div className="relative w-72">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input className="pl-8" placeholder="Buscar beneficiario..." value={buscarSup} onChange={(e) => setBuscarSup(e.target.value)} data-testid="buscar-suplidores" />
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                {estado?.ultima_sync_catalogos && <span>Última sincronización: {fmtFecha(estado.ultima_sync_catalogos)}</span>}
                <Button variant="outline" size="sm" onClick={syncAhora} data-testid="sync-now-btn"><RefreshCw className="w-4 h-4 mr-1" />Sincronizar ahora</Button>
              </div>
            </div>
            <Card className="shadow-md overflow-hidden"><CardContent className="p-0 max-h-[65vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white"><th className="p-3 font-semibold">Beneficiario</th><th className="p-3 font-semibold">Tipo</th></tr></thead>
                <tbody data-testid="suplidores-body">
                  {supFiltrados.length === 0 ? (
                    <tr><td colSpan={2} className="p-8 text-center text-slate-400">Aún no hay beneficiarios sincronizados desde QuickBooks</td></tr>
                  ) : supFiltrados.map((s, i) => (
                    <tr key={s.list_id} className={`border-b ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`}>
                      <td className="p-2.5 dark:text-white">{s.nombre}</td>
                      <td className="p-2.5"><Badge variant="outline" className="text-xs">{s.tipo || "Suplidor"}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* ===== CUENTAS ===== */}
          <TabsContent value="cuentas" className="mt-4 space-y-4">
            <div className="relative w-72">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input className="pl-8" placeholder="Buscar cuenta..." value={buscarCta} onChange={(e) => setBuscarCta(e.target.value)} data-testid="buscar-cuentas" />
            </div>
            <Card className="shadow-md overflow-hidden"><CardContent className="p-0 max-h-[65vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10"><tr className="bg-[#0C314C] text-left text-white">
                  <th className="p-3 font-semibold">Cuenta</th><th className="p-3 font-semibold">Tipo</th>
                </tr></thead>
                <tbody data-testid="cuentas-body">
                  {ctaFiltradas.length === 0 ? (
                    <tr><td colSpan={2} className="p-8 text-center text-slate-400">Aún no hay cuentas sincronizadas desde QuickBooks</td></tr>
                  ) : ctaFiltradas.map((c, i) => (
                    <tr key={c.list_id} className={`border-b ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`}>
                      <td className="p-2.5 dark:text-white">{c.nombre}</td>
                      <td className="p-2.5"><Badge variant="outline" className="text-xs">{c.tipo}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* ===== FORMATO DE IMPRESIÓN ===== */}
          <TabsContent value="formato" className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-500">Arrastre los campos sobre la hoja o ajuste sus posiciones manualmente (en milímetros desde la esquina superior izquierda). Imprima el PDF de prueba sobre un cheque para verificar la alineación.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetLayout} data-testid="layout-reset-btn"><RotateCcw className="w-4 h-4 mr-1" />Restaurar</Button>
                <Button variant="outline" onClick={pdfPrueba} data-testid="layout-pdf-prueba-btn"><Printer className="w-4 h-4 mr-1" />PDF de prueba</Button>
                <Button onClick={guardarLayout} disabled={guardandoLayout || !layout} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="layout-guardar-btn">
                  {guardandoLayout && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Guardar formato
                </Button>
              </div>
            </div>
            {layout && (
              <div className="flex flex-wrap gap-4">
                <Card className="shadow-md overflow-hidden"><CardContent className="p-3">
                  <div ref={canvasRef} onMouseMove={moverDrag} onMouseUp={finDrag} onMouseLeave={finDrag}
                    className="relative bg-white border border-slate-300 rounded shadow-inner select-none overflow-hidden"
                    style={{ width: 216 * ESCALA, height: 135 * ESCALA }} data-testid="layout-canvas">
                    <div className="absolute left-0 right-0 border-b border-dashed border-slate-300" style={{ top: 89 * ESCALA }} />
                    <span className="absolute text-[9px] text-slate-300" style={{ left: 4, top: 89 * ESCALA - 14 }}>zona del cheque ↑ (aprox. 3.5")</span>
                    {CAMPOS_LAYOUT.map(([id, label, muestra]) => {
                      const c = layout[id];
                      if (!c || !c.visible) return null;
                      return (
                        <div key={id} onMouseDown={(e) => startDrag(e, id)}
                          className={`absolute cursor-move whitespace-nowrap px-0.5 rounded-sm ${campoSel === id ? "ring-2 ring-blue-500 bg-blue-50 text-blue-900 z-10" : "hover:bg-amber-50 text-slate-700"}`}
                          style={{ left: c.x * ESCALA, top: c.y * ESCALA - c.size, fontSize: Math.max(7, c.size * 1.1), fontWeight: ["empresa", "numero", "beneficiario", "monto"].includes(id) ? 700 : 400 }}
                          title={label} data-testid={`layout-campo-${id}`}>
                          {muestra}
                        </div>
                      );
                    })}
                  </div>
                </CardContent></Card>
                <Card className="shadow-md flex-1 min-w-[260px]"><CardContent className="p-4 space-y-3">
                  <div>
                    <Label>Campo</Label>
                    <select className="w-full h-10 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm" value={campoSel} onChange={(e) => setCampoSel(e.target.value)} data-testid="layout-campo-select">
                      {CAMPOS_LAYOUT.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                    </select>
                  </div>
                  {layout[campoSel] && (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        <div><Label className="text-xs">X (mm)</Label>
                          <Input type="number" step="0.5" value={layout[campoSel].x} onChange={(e) => setCampo(campoSel, "x", parseFloat(e.target.value) || 0)} data-testid="layout-x" /></div>
                        <div><Label className="text-xs">Y (mm)</Label>
                          <Input type="number" step="0.5" value={layout[campoSel].y} onChange={(e) => setCampo(campoSel, "y", parseFloat(e.target.value) || 0)} data-testid="layout-y" /></div>
                        <div><Label className="text-xs">Tamaño (pt)</Label>
                          <Input type="number" step="0.5" min={5} max={24} value={layout[campoSel].size} onChange={(e) => setCampo(campoSel, "size", parseFloat(e.target.value) || 8)} data-testid="layout-size" /></div>
                      </div>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={layout[campoSel].visible} onChange={(e) => setCampo(campoSel, "visible", e.target.checked)} data-testid="layout-visible" />
                        Imprimir este campo
                      </label>
                    </>
                  )}
                  <div className="border-t pt-2 space-y-1 max-h-56 overflow-y-auto">
                    {CAMPOS_LAYOUT.map(([id, label]) => (
                      <label key={id} className={`flex items-center justify-between gap-2 text-xs px-2 py-1 rounded cursor-pointer ${campoSel === id ? "bg-blue-50 dark:bg-blue-900/20" : ""}`} onClick={() => setCampoSel(id)}>
                        <span className="flex items-center gap-1.5">
                          <input type="checkbox" checked={layout[id]?.visible ?? true} onChange={(e) => setCampo(id, "visible", e.target.checked)} data-testid={`layout-check-${id}`} />
                          {label}
                        </span>
                        <span className="text-slate-400 font-mono">{layout[id]?.x}, {layout[id]?.y}</span>
                      </label>
                    ))}
                  </div>
                </CardContent></Card>
              </div>
            )}
          </TabsContent>

          {/* ===== CONEXIÓN (admin) ===== */}
          {isAdmin && (
            <TabsContent value="conexion" className="mt-4 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="shadow-md"><CardContent className="p-5 space-y-3">
                  <h3 className="font-semibold dark:text-white flex items-center gap-2"><Plug className="w-4 h-4" />Credenciales del Web Connector</h3>
                  <div><Label>Usuario</Label><Input value={configForm.username} onChange={(e) => setConfigForm({ ...configForm, username: e.target.value })} placeholder="qasesores" data-testid="qb-username" /></div>
                  <div><Label>Clave de conexión {config?.configurado && <span className="text-xs text-slate-400">(dejar vacío para no cambiarla)</span>}</Label>
                    <Input type="password" value={configForm.password} onChange={(e) => setConfigForm({ ...configForm, password: e.target.value })} data-testid="qb-password" /></div>
                  <div><Label>Sincronizar cada (minutos)</Label>
                    <Input type="number" min={1} max={60} value={configForm.run_every_min} onChange={(e) => setConfigForm({ ...configForm, run_every_min: parseInt(e.target.value) || 10 })} data-testid="qb-runevery" /></div>
                  <div className="flex gap-2">
                    <Button onClick={guardarConfig} disabled={guardandoCfg || !configForm.username.trim()} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="qb-guardar-config-btn">
                      {guardandoCfg && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Guardar
                    </Button>
                    <Button variant="outline" onClick={descargarQwc} data-testid="qb-descargar-qwc-btn"><FileDown className="w-4 h-4 mr-1" />Descargar archivo .qwc</Button>
                  </div>
                  {estado && (
                    <div className="text-sm text-slate-500 space-y-1 pt-2 border-t">
                      <div className="flex items-center gap-2">Estado: {estado.ultima_conexion ? <Badge className="bg-emerald-100 text-emerald-800">Conectado — {fmtFecha(estado.ultima_conexion)}</Badge> : <Badge className="bg-slate-200 text-slate-600">Sin conexiones aún</Badge>}</div>
                      {estado.company_file && <p className="text-xs">Archivo: {estado.company_file}</p>}
                      {estado.ultimo_error && <p className="text-xs text-red-600">Último error: {estado.ultimo_error}</p>}
                      <p className="text-xs">{estado.cuentas} cuentas · {estado.suplidores} suplidores sincronizados</p>
                    </div>
                  )}
                </CardContent></Card>
                <Card className="shadow-md"><CardContent className="p-5 text-sm space-y-2">
                  <h3 className="font-semibold dark:text-white">Pasos para conectar (en la PC/servidor con QuickBooks)</h3>
                  <ol className="list-decimal ml-5 space-y-1.5 text-slate-600 dark:text-slate-300">
                    <li>Defina aquí el <strong>usuario</strong> y la <strong>clave</strong> de conexión y guarde.</li>
                    <li>Descargue el archivo <strong>.qwc</strong> y cópielo a la PC donde está QuickBooks Enterprise 2018.</li>
                    <li>Abra QuickBooks con el archivo de la empresa y como usuario Admin.</li>
                    <li>Abra el <strong>QuickBooks Web Connector</strong> (viene con QuickBooks: Archivo → App Management → Update Web Services, o busque "Web Connector" en Windows).</li>
                    <li>Haga clic en <strong>Add an Application</strong> y seleccione el archivo .qwc descargado.</li>
                    <li>QuickBooks pedirá autorizar el acceso: elija <strong>"Yes, always; allow access even if QuickBooks is not running"</strong>.</li>
                    <li>En el Web Connector, escriba la <strong>clave de conexión</strong> en la columna Password y marque la casilla de la aplicación.</li>
                    <li>Presione <strong>Update Selected</strong> para la primera sincronización (cuentas y suplidores aparecerán en las pestañas).</li>
                  </ol>
                  <p className="text-xs text-slate-400 pt-1">La sincronización corre automáticamente cada {configForm.run_every_min} min mientras la PC esté encendida. Los cheques programados se registran en QuickBooks en cada conexión.</p>
                </CardContent></Card>
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* ===== Dialog Traer Cheque de QuickBooks ===== */}
        <Dialog open={showQB} onOpenChange={(v) => { if (!v) setShowQB(false); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cheque-qb-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-[#0C314C]" />Imprimir Cheque desde QuickBooks</DialogTitle>
              <DialogDescription>
                Seleccione el cheque ya registrado en QuickBooks: el sistema trae el beneficiario, concepto y desglose contable para imprimirlo (flujo QB Enlace).
              </DialogDescription>
            </DialogHeader>
            {chequesQB.length === 0 ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-3 text-sm text-amber-800 dark:text-amber-200" data-testid="sin-cheques-qb">
                Aún no hay cheques sincronizados desde QuickBooks. Registre el cheque en QuickBooks y espere la próxima conexión del Web Connector (o use "Sincronizar ahora" en la pestaña Suplidores).
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                  <Input className="pl-8" placeholder="Buscar beneficiario, número de cheque o monto..." value={qbBusqueda}
                    onChange={(e) => setQbBusqueda(e.target.value)} data-testid="qbsel-busqueda" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Cuenta de banco</Label>
                    <ComboSearch value={qbSel.banco} onChange={(v) => setQbSel({ banco: v, beneficiario: "", txn_id: "" })}
                      opciones={bancosQB.map((b) => ({ value: b, label: b }))} placeholder="Todas" testid="qbsel-banco" />
                  </div>
                  <div>
                    <Label>Beneficiario</Label>
                    <ComboSearch value={qbSel.beneficiario} onChange={(v) => setQbSel({ ...qbSel, beneficiario: v, txn_id: "" })}
                      opciones={benefQB.map((b) => ({ value: b, label: b }))} placeholder="Todos" testid="qbsel-beneficiario" />
                  </div>
                </div>
                <div>
                  <Label>Cheque registrado en QuickBooks ({chequesFiltradosQB.length})</Label>
                  <ComboSearch value={qbSel.txn_id} onChange={(v) => setQbSel({ ...qbSel, txn_id: v })}
                    opciones={chequesFiltradosQB.map((c) => ({ value: c.txn_id, label: `No. ${c.numero || "S/N"} — ${fmtDia(c.fecha)} — ${c.beneficiario} — ${fmtMonto(c.monto)}` }))}
                    placeholder="Seleccione el cheque..." testid="qbsel-cheque" />
                </div>
                {chequeSelQB && (
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2 text-sm" data-testid="qbsel-detalle">
                    <div className="grid grid-cols-2 gap-1.5">
                      <p><span className="text-slate-500">Banco:</span> <strong className="dark:text-white">{chequeSelQB.banco}</strong></p>
                      <p><span className="text-slate-500">No.:</span> <strong className="dark:text-white">{chequeSelQB.numero || "S/N"}</strong></p>
                      <p><span className="text-slate-500">Fecha:</span> <strong className="dark:text-white">{fmtDia(chequeSelQB.fecha)}</strong></p>
                      <p><span className="text-slate-500">Monto:</span> <strong className="dark:text-white">{fmtMonto(chequeSelQB.monto)}</strong></p>
                    </div>
                    {chequeSelQB.concepto && <p><span className="text-slate-500">Concepto:</span> {chequeSelQB.concepto}</p>}
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-slate-500 border-b"><th className="py-1">Desglose contable</th><th className="py-1">Memo</th><th className="py-1 text-right">Monto</th></tr></thead>
                      <tbody>
                        {(chequeSelQB.lineas || []).map((l, i) => (
                          <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                            <td className="py-1 dark:text-white">{l.cuenta}</td><td className="py-1 text-slate-500">{l.memo}</td><td className="py-1 text-right">{fmtMonto(l.monto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowQB(false)}>Cancelar</Button>
              <Button onClick={importarCheque} disabled={importando || !qbSel.txn_id} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="importar-cheque-btn">
                {importando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}Traer e Imprimir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog Nuevo/Editar Cheque ===== */}
        <Dialog open={showForm} onOpenChange={(v) => { if (!v) setShowForm(false); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cheque-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-[#0C314C]" />{editId ? "Editar Cheque" : "Nuevo Cheque"}</DialogTitle>
              <DialogDescription>Programe el cheque; luego podrá registrarlo en QuickBooks e imprimirlo.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Cuenta de banco *</Label>
                  {cuentasBanco.length > 0 ? (
                    <ComboSearch value={form.cuenta_banco} onChange={(v) => setForm({ ...form, cuenta_banco: v })}
                      opciones={cuentasBanco.map((c) => ({ value: c.nombre, label: c.nombre }))} testid="cheque-banco" />
                  ) : (
                    <Input value={form.cuenta_banco} onChange={(e) => setForm({ ...form, cuenta_banco: e.target.value })} placeholder="Nombre exacto de la cuenta en QB" data-testid="cheque-banco" />
                  )}
                </div>
                <div>
                  <Label>Beneficiario *</Label>
                  {suplidores.length > 0 ? (
                    <ComboSearch value={form.beneficiario} onChange={(v) => setForm({ ...form, beneficiario: v })}
                      opciones={suplidores.map((s) => ({ value: s.nombre, label: s.tipo && s.tipo !== "Suplidor" ? `${s.nombre} (${s.tipo})` : s.nombre }))}
                      placeholder="Beneficiario..." testid="cheque-beneficiario" />
                  ) : (
                    <Input value={form.beneficiario} onChange={(e) => setForm({ ...form, beneficiario: e.target.value })} placeholder="Beneficiario (nombre exacto en QB)" data-testid="cheque-beneficiario" />
                  )}
                </div>
                <div><Label>Fecha *</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="cheque-fecha" /></div>
                <div><Label>No. de cheque <span className="text-xs text-slate-400">(vacío = QB lo asigna al imprimir)</span></Label>
                  <Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} data-testid="cheque-numero" /></div>
              </div>
              <div><Label>Concepto / Memo</Label><Textarea rows={2} value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} data-testid="cheque-concepto" /></div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label>Desglose contable *</Label>
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...form, lineas: [...form.lineas, { cuenta: "", monto: "", memo: "" }] })} data-testid="agregar-linea-btn"><Plus className="w-3.5 h-3.5 mr-1" />Línea</Button>
                </div>
                <div className="space-y-2">
                  {form.lineas.map((l, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      {cuentasGasto.length > 0 ? (
                        <ComboSearch className="flex-1" value={l.cuenta} onChange={(v) => setLinea(i, "cuenta", v)}
                          opciones={cuentasGasto.map((c) => ({ value: c.nombre, label: `${c.nombre} (${c.tipo})` }))}
                          placeholder="Cuenta de gasto..." testid={`linea-cuenta-${i}`} />
                      ) : (
                        <Input className="flex-1 h-9" placeholder="Cuenta de gasto (nombre exacto en QB)" value={l.cuenta} onChange={(e) => setLinea(i, "cuenta", e.target.value)} data-testid={`linea-cuenta-${i}`} />
                      )}
                      <Input className="w-32 h-9" type="number" step="0.01" placeholder="Monto" value={l.monto} onChange={(e) => setLinea(i, "monto", e.target.value)} data-testid={`linea-monto-${i}`} />
                      <Input className="w-40 h-9" placeholder="Memo" value={l.memo} onChange={(e) => setLinea(i, "memo", e.target.value)} data-testid={`linea-memo-${i}`} />
                      {form.lineas.length > 1 && (
                        <Button size="sm" variant="ghost" className="h-9 px-2 text-red-500" onClick={() => setForm({ ...form, lineas: form.lineas.filter((_, idx) => idx !== i) })} data-testid={`quitar-linea-${i}`}><X className="w-4 h-4" /></Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-right text-sm font-semibold mt-2 dark:text-white" data-testid="cheque-total">Total: {fmtMonto(totalForm)}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={guardarCheque} disabled={guardando || !form.cuenta_banco.trim() || !form.beneficiario.trim() || totalForm <= 0} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="guardar-cheque-btn">
                {guardando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{editId ? "Guardar cambios" : "Guardar borrador"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog eliminar ===== */}
        <Dialog open={!!eliminar} onOpenChange={(v) => { if (!v) setEliminar(null); }}>
          <DialogContent className="max-w-md" data-testid="eliminar-cheque-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" />Eliminar cheque</DialogTitle>
              <DialogDescription>¿Eliminar el cheque a <strong>{eliminar?.beneficiario}</strong> por {fmtMonto(eliminar?.monto)}? {eliminar?.estado === "registrado" && "Ya está registrado en QuickBooks; esta acción solo lo elimina de la plataforma."}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEliminar(null)}>Cancelar</Button>
              <Button onClick={eliminarCheque} className="bg-red-600 hover:bg-red-700" data-testid="confirmar-eliminar-cheque-btn">Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
